import { defineStore } from 'pinia'
import fetchWrapper, { api } from '@/helpers/fetch-wrapper'
import router from '@/router'
import parseJwt from '@/helpers/parse-jwt'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { PushNotifications } from '@capacitor/push-notifications'
import { networkHandler } from '@/helpers/network-handler'

const baseRoute = '/auth'

const isNetworkError = (error) => {
    return (
        !error.response &&
        (error.message === 'Network Error' ||
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED')
    )
}

export const useAuthStore = defineStore({
    id: 'auth',
    state: () => ({
        user: null,
        refreshTokenTimer: null,
        companiesList: [],
        isRefreshing: false,
        refreshPromise: null
    }),
    actions: {
        async login(data) {
            try {
                const response = await fetchWrapper.post(`${baseRoute}/login`, {
                    ...data,
                    is_mobile: true
                })

                await this.handleLoginSuccess(response)
            } catch (error) {
                console.error('Login failed:', error)
            }
        },

        async handleLoginSuccess(response, redirect = true) {
            this.setUser(parseJwt(response.access_token))
            await this.storeRefreshToken(response.refresh_token)
            this.startRefreshTokenTimer()

            if (response.companies_for_select?.length) {
                this.companiesList = response.companies_for_select
                await router.push({ name: 'companySelectPage' })
            } else if (redirect) {
                await router.push({ name: 'homePage' })
            }
        },

        async logout() {
            try {
                await fetchWrapper.post(
                    `${baseRoute}/logout`,
                    {},
                    {
                        headers: {
                            Authorization: this.user.access_token
                        }
                    }
                )
            } catch (error) {
                console.error('Logout failed:', error)
            } finally {
                this.clearUserSession()
            }
        },

        async refreshToken() {
            // Якщо вже йде процес refresh, повертаємо існуючий проміс
            if (this.isRefreshing && this.refreshPromise) {
                return this.refreshPromise
            }

            this.isRefreshing = true
            this.refreshPromise = this._performRefresh()

            try {
                await this.refreshPromise
            } catch (error) {
                // Якщо помилка мережі, перепланувуємо refresh на пізніше
                if (isNetworkError(error)) {
                    console.log('Network error during refresh, will retry when connection restored')
                    // Додаємо слухач для повтору refresh коли з'явиться інтернет
                    const retryRefresh = async (isOnline) => {
                        if (isOnline && this.user?.access_token) {
                            networkHandler.removeListener(retryRefresh)
                            await this.refreshToken()
                        }
                    }

                    networkHandler.addListener(retryRefresh)
                    // Перепланувуємо спробу через 30 секунд якщо інтернет не з'явиться
                    setTimeout(() => {
                        if (this.user?.access_token && !this.isRefreshing) {
                            this.refreshToken()
                        }
                    }, 30000)
                }
                // Не пробрасуємо помилку далі для мережевих помилок
                if (!isNetworkError(error)) {
                    throw error
                }
            } finally {
                this.isRefreshing = false
                this.refreshPromise = null
            }
        },

        async _performRefresh() {
            try {
                const refreshToken = await this.getStoredRefreshToken()

                if (!refreshToken) {
                    throw new Error('No refresh token available')
                }

                const response = await fetchWrapper.post(
                    `${import.meta.env.VITE_API_URL + baseRoute}/refresh`,
                    {
                        refresh_token: refreshToken,
                        services: [
                            import.meta.env.VITE_SERVICE_TYPE,
                            import.meta.env.VITE_FINANCIAL_SERVICE
                        ],
                        is_mobile: true
                    }
                )

                // Нормалізуємо відповідь: якщо прийшла через чергу networkHandler,
                // вона буде обгорнута в response.data, інакше вже розгорнута
                const data = response.data ? response.data : response

                await this.handleLoginSuccess(data, false)
            } catch (error) {
                // Не розлогінюємо при помилках мережі
                if (!isNetworkError(error)) {
                    console.error('Refresh token failed:', error.message ? error.message : error)
                    this.clearUserSession(true)
                } else {
                    console.log(
                        'Network error during token refresh, will retry when connection restored'
                    )
                }
                throw error
            }
        },

        async refreshTokenDirect() {
            try {
                const refreshToken = await this.getStoredRefreshToken()

                if (!refreshToken) {
                    return { success: false, error: 'No refresh token available' }
                }

                // Перевіряємо інтернет перед спробою
                const isOnline = networkHandler.checkConnection()

                if (!isOnline) {
                    return { success: false, error: 'No internet connection' }
                }

                const response = await api.post(
                    `${import.meta.env.VITE_API_URL + baseRoute}/refresh`,
                    {
                        refresh_token: refreshToken,
                        services: [
                            import.meta.env.VITE_SERVICE_TYPE,
                            import.meta.env.VITE_FINANCIAL_SERVICE
                        ],
                        is_mobile: true
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        withCredentials: true
                    }
                )

                await this.handleLoginSuccess(response.data, false)

                return { success: true }
            } catch (error) {
                console.error('Direct refresh token failed:', error.message ? error.message : error)

                // Не розлогінюємо при помилках мережі
                if (isNetworkError(error)) {
                    return { success: false, error: 'Network error', isNetworkError: true }
                }

                return { success: false, error: error.message || 'Token refresh failed' }
            }
        },

        async storeRefreshToken(token) {
            await SecureStoragePlugin.set({ key: 'refresh_token', value: token })
        },

        async getStoredRefreshToken() {
            const { value } = await SecureStoragePlugin.get({ key: 'refresh_token' })

            return value
        },

        clearUserSession(disablePush = false) {
            this.stopRefreshTokenTimer()
            this.user = {}
            SecureStoragePlugin.remove({ key: 'refresh_token' })

            // Очищаємо чергу запитів
            networkHandler.clearPendingRequests()

            if (router.currentRoute.value.meta.requiresAuth) {
                router.push({ name: 'loginPage' })
            }

            if (disablePush) {
                PushNotifications.unregister()
            }
        },

        setUser(user) {
            this.user = user
        },

        startRefreshTokenTimer() {
            const expires = new Date(this.user.exp * 1000)
            const timeout = expires.getTime() - Date.now() - 60 * 1000

            this.refreshTokenTimer = setTimeout(() => this.refreshToken(), timeout)
        },

        stopRefreshTokenTimer() {
            clearTimeout(this.refreshTokenTimer)
        },

        isUserLoggedIn() {
            return !!this.user?.access_token
        }
    }
})
